import { hydraUiApi } from '@/services/api';
import { codeToCountry, countryToCode } from '@/utils/countries';
import type { CreditCardFormAddress } from './credit-card-form-submit';

export type AddressValidationResult = {
  isValid: boolean;
  correctedAddress: CreditCardFormAddress | null;
};

type ParsedGoogleResult = {
  address: CreditCardFormAddress;
  isCorrected: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function addressesEqual(a: CreditCardFormAddress, b: CreditCardFormAddress): boolean {
  return (
    a.addressLine1 === b.addressLine1 &&
    a.addressLine2 === b.addressLine2 &&
    a.city === b.city &&
    a.state === b.state &&
    a.zipCode === b.zipCode &&
    a.country === b.country
  );
}

/**
 * Parses the Google Address Validation result into our address shape and whether it represents a
 * correction. Returns null when the result lacks a verdict or postal address. Ported from the Ember
 * `address-validation` service; the response is loosely typed, so every field is read defensively.
 */
export function parseGoogleResult(oldAddress: CreditCardFormAddress, googleResult: unknown): ParsedGoogleResult | null {
  const result = asRecord(googleResult);
  const verdict = asRecord(result?.verdict);
  const postalAddress = asRecord(asRecord(result?.address)?.postalAddress);

  if (verdict == null || postalAddress == null) {
    return null;
  }

  const addressLines = Array.isArray(postalAddress.addressLines) ? postalAddress.addressLines : [];
  const convertedAddress: CreditCardFormAddress = {
    addressLine1: asString(addressLines[0]),
    addressLine2: asString(addressLines[1]),
    state: asString(postalAddress.administrativeArea),
    city: asString(postalAddress.locality),
    zipCode: asString(postalAddress.postalCode),
    country: codeToCountry(asString(postalAddress.regionCode)),
  };

  let isCorrected =
    verdict.hasSpellCorrectedComponents === true ||
    verdict.hasReplacedComponents === true ||
    verdict.hasInferredComponents === true;

  // Google sometimes silences a single-character fix, returning the address as if unchanged, so
  // compare the addresses directly when it reports a complete-but-unflagged result.
  if (verdict.addressComplete === true && !isCorrected) {
    isCorrected = !addressesEqual(oldAddress, convertedAddress);
  }

  return { address: convertedAddress, isCorrected };
}

/**
 * Interprets a Google validation result into a validity verdict + optional corrected address,
 * mirroring the Ember `address-validation` service's `validateAddress`.
 */
export function interpretValidation(oldAddress: CreditCardFormAddress, googleResult: unknown): AddressValidationResult {
  const parsed = parseGoogleResult(oldAddress, googleResult);
  if (parsed == null) {
    return { isValid: false, correctedAddress: null };
  }

  if (parsed.isCorrected) {
    return { isValid: false, correctedAddress: parsed.address };
  }

  const verdict = asRecord(asRecord(googleResult)?.verdict);
  return { isValid: verdict?.addressComplete === true, correctedAddress: null };
}

/**
 * Validates a billing address against the account's address-validation endpoint. Best-effort: any
 * error resolves to null so it never blocks the card submission (matching Ember).
 */
export async function validateBillingAddress(
  accountId: number,
  address: CreditCardFormAddress,
): Promise<AddressValidationResult | null> {
  try {
    const response = await hydraUiApi.accounts.validateAddress({
      params: { path: { account_id: String(accountId) } },
      body: {
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        country: countryToCode(address.country),
      },
    });

    return interpretValidation(address, asRecord(response)?.result);
  } catch {
    // Don't block the card submission on a validation error.
    return null;
  }
}
